import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProject } from '@/contexts/ProjectContext';
import { getDeliveryStatus, setLifecyclePhase } from '@/lib/api';
import { ChevronDown } from 'lucide-react';

const PHASES = [
  { label: 'Intake', number: 1 },
  { label: 'Validation', number: 2 },
  { label: 'Architecture', number: 3 },
  { label: 'Decomposition', number: 4 },
  { label: 'Implementation', number: 5 },
  { label: 'Verification', number: 6 },
  { label: 'Convergence', number: 7 },
  { label: 'Deployment', number: 8 },
  { label: 'Go-Live', number: 9 },
  { label: 'Support', number: 10 },
];

/**
 * OddLifecyclePanel
 *
 * Renders a vertical 10-phase stepper showing current project phase.
 * Clicking a phase mutates the delivery status (PATCH /api/codebases/:id/delivery).
 *
 * Visual: Circle (past=green filled, current=primary filled, future=gray outline)
 *         + connecting line between phases
 *         + label (current=primary text, inactive=secondary text)
 */
export function OddLifecyclePanel(): React.ReactElement | null {
  const { selectedProjectId } = useProject();
  const queryClient = useQueryClient();

  const { data: delivery, isLoading } = useQuery({
    queryKey: ['delivery-status', selectedProjectId],
    queryFn: () => {
      if (!selectedProjectId) throw new Error('No project selected');
      return getDeliveryStatus(selectedProjectId);
    },
    enabled: !!selectedProjectId,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const phaseMutation = useMutation({
    mutationFn: (phase: number) => {
      if (!selectedProjectId) throw new Error('No project selected');
      return setLifecyclePhase(selectedProjectId, phase);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['delivery-status', selectedProjectId] });
    },
  });

  const currentPhaseIndex = delivery?.lifecyclePhase?.current
    ? delivery.lifecyclePhase.current - 1
    : -1;

  // Hide the panel when no project is selected; the outer page surfaces
  // the "Choose a project" prompt. See OutcomeHealthPanel for the same
  // pattern.
  if (!selectedProjectId) return null;
  if (isLoading) {
    return (
      <div className="text-xs text-text-tertiary">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
          Delivery Phase
        </div>
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
        Delivery Phase
      </div>

      <div className="space-y-1">
        {PHASES.map((phase, idx) => {
          const isPast = idx < currentPhaseIndex;
          const isCurrent = idx === currentPhaseIndex;

          return (
            <div key={phase.number}>
              {/* Phase circle + label */}
              <button
                onClick={() => {
                  phaseMutation.mutate(phase.number);
                }}
                disabled={phaseMutation.isPending}
                className="flex w-full items-start gap-2 px-2 py-1.5 text-left transition-colors hover:bg-surface-elevated disabled:opacity-50"
              >
                {/* Dot */}
                <div className="relative mt-0.5 flex-shrink-0">
                  <div
                    className={`h-5 w-5 rounded-full border-2 transition-colors ${
                      isPast
                        ? 'border-success bg-success'
                        : isCurrent
                          ? 'border-primary bg-primary'
                          : 'border-border bg-transparent'
                    }`}
                  />
                  {isCurrent && (
                    <div className="absolute inset-0 animate-pulse rounded-full border-2 border-primary" />
                  )}
                </div>

                {/* Label */}
                <div className="flex flex-1 flex-col">
                  <span
                    className={`text-xs font-medium ${
                      isCurrent ? 'text-text-primary' : 'text-text-secondary'
                    }`}
                  >
                    {phase.label}
                  </span>
                  <span className="text-[10px] text-text-tertiary">{phase.number}/10</span>
                </div>

                {isCurrent && (
                  <ChevronDown className="mt-0.5 h-3 w-3 text-primary" strokeWidth={3} />
                )}
              </button>

              {/* Connecting line to next phase */}
              {idx < PHASES.length - 1 && <div className="ml-2 border-l border-border py-0.5" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
